import React, { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import UserEmailBadge from './UserEmailBadge'
import { calculateAllMetrics } from '../utils/financialCalculations'
import { calculateHealthScore } from '../utils/businessHealthScore'
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ScatterChart,
  Scatter
} from 'recharts'

const AdminAnalyticsDashboard = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [businesses, setBusinesses] = useState([])
  const [aggregateData, setAggregateData] = useState(null)
  const [selectedTimeframe, setSelectedTimeframe] = useState('current')
  const [selectedView, setSelectedView] = useState('overview')

  const handleLogout = async () => {
    const { error } = await signOut()
    if (error) {
      console.error('Error signing out:', error)
    }
    navigate('/login')
  }

  useEffect(() => {
    fetchAnalyticsData()
  }, [selectedTimeframe])

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true)

      // Fetch all businesses with their metrics
      const { data: businessList, error: businessError } = await supabase
        .from('businesses')
        .select(`
          id,
          business_name,
          industry,
          created_at,
          status
        `)
        .eq('status', 'active')

      if (businessError) throw businessError

      // Fetch metrics for all businesses
      const currentDate = new Date()
      const currentMonth = currentDate.getMonth() + 1
      const currentYear = currentDate.getFullYear()

      let startDate, endDate
      if (selectedTimeframe === 'current') {
        startDate = new Date(currentYear, currentMonth - 1, 1)
        endDate = new Date(currentYear, currentMonth, 0)
      } else if (selectedTimeframe === 'last3months') {
        startDate = new Date(currentYear, currentMonth - 4, 1)
        endDate = new Date(currentYear, currentMonth, 0)
      } else {
        startDate = new Date(currentYear - 1, currentMonth - 1, 1)
        endDate = new Date(currentYear, currentMonth, 0)
      }

      const { data: metrics, error: metricsError } = await supabase
        .from('monthly_metrics')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())

      if (metricsError) throw metricsError

      // Process data
      const processedData = processAnalyticsData(businessList, metrics)
      setBusinesses(processedData.businesses)
      setAggregateData(processedData.aggregate)

    } catch (err) {
      console.error('Error fetching analytics data:', err)
    } finally {
      setLoading(false)
    }
  }

  const processAnalyticsData = (businessList, metrics) => {
    const businessMetrics = {}

    // Group metrics by business
    metrics.forEach(metric => {
      if (!businessMetrics[metric.business_id]) {
        businessMetrics[metric.business_id] = []
      }
      businessMetrics[metric.business_id].push(metric)
    })

    // Calculate aggregated metrics for each business
    const businesses = businessList.map(business => {
      const businessData = businessMetrics[business.id] || []
      
      if (businessData.length === 0) {
        return {
          ...business,
          hasData: false,
          healthScore: 0,
          totalRevenue: 0,
          totalExpenses: 0,
          avgProfitMargin: 0,
          avgConversionRate: 0,
          metricsCount: 0
        }
      }

      // Calculate current month metrics (most recent)
      const latestMetric = businessData
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
      
      const calculatedMetrics = calculateAllMetrics(latestMetric)
      const healthData = calculateHealthScore(calculatedMetrics)

      // Calculate totals across timeframe
      const totalRevenue = businessData.reduce((sum, m) => sum + (m.revenue || 0), 0)
      const totalExpenses = businessData.reduce((sum, m) => sum + (m.expenses || 0), 0)
      const avgProfitMargin = businessData.reduce((sum, m) => {
        const metrics = calculateAllMetrics(m)
        return sum + (metrics.profitMargin || 0)
      }, 0) / businessData.length

      const avgConversionRate = businessData.reduce((sum, m) => {
        const metrics = calculateAllMetrics(m)
        return sum + (metrics.conversionRate || 0)
      }, 0) / businessData.length

      return {
        ...business,
        hasData: true,
        healthScore: healthData.score,
        healthStatus: healthData.status,
        totalRevenue,
        totalExpenses,
        totalNetProfit: totalRevenue - totalExpenses,
        avgProfitMargin: Math.round(avgProfitMargin * 10) / 10,
        avgConversionRate: Math.round(avgConversionRate * 10) / 10,
        metricsCount: businessData.length,
        latestMetrics: calculatedMetrics,
        rawData: businessData
      }
    })

    // Calculate aggregate statistics
    const businessesWithData = businesses.filter(b => b.hasData)
    const aggregate = {
      totalBusinesses: businesses.length,
      activeBusinesses: businessesWithData.length,
      totalRevenue: businessesWithData.reduce((sum, b) => sum + b.totalRevenue, 0),
      totalExpenses: businessesWithData.reduce((sum, b) => sum + b.totalExpenses, 0),
      averageHealthScore: businessesWithData.length > 0 
        ? Math.round(businessesWithData.reduce((sum, b) => sum + b.healthScore, 0) / businessesWithData.length)
        : 0,
      industryBreakdown: getIndustryBreakdown(businessesWithData),
      performanceDistribution: getPerformanceDistribution(businessesWithData),
      topPerformers: businessesWithData
        .sort((a, b) => b.healthScore - a.healthScore)
        .slice(0, 5),
      bottomPerformers: businessesWithData
        .filter(b => b.healthScore > 0)
        .sort((a, b) => a.healthScore - b.healthScore)
        .slice(0, 5)
    }

    return { businesses, aggregate }
  }

  const getIndustryBreakdown = (businesses) => {
    const breakdown = {}
    businesses.forEach(business => {
      const industry = business.industry || 'Other'
      if (!breakdown[industry]) {
        breakdown[industry] = {
          count: 0,
          totalRevenue: 0,
          avgHealthScore: 0
        }
      }
      breakdown[industry].count += 1
      breakdown[industry].totalRevenue += business.totalRevenue
      breakdown[industry].avgHealthScore += business.healthScore
    })

    // Calculate averages and format for charts
    return Object.keys(breakdown).map(industry => ({
      name: industry,
      count: breakdown[industry].count,
      totalRevenue: breakdown[industry].totalRevenue,
      avgHealthScore: Math.round(breakdown[industry].avgHealthScore / breakdown[industry].count),
      avgRevenue: Math.round(breakdown[industry].totalRevenue / breakdown[industry].count)
    }))
  }

  const getPerformanceDistribution = (businesses) => {
    const distribution = {
      excellent: 0, // 85+
      good: 0,      // 70-84
      warning: 0,   // 50-69
      poor: 0       // <50
    }

    businesses.forEach(business => {
      if (business.healthScore >= 85) distribution.excellent += 1
      else if (business.healthScore >= 70) distribution.good += 1
      else if (business.healthScore >= 50) distribution.warning += 1
      else distribution.poor += 1
    })

    return [
      { name: 'Excellent (85+)', value: distribution.excellent, color: '#10B981' },
      { name: 'Good (70-84)', value: distribution.good, color: '#3B82F6' },
      { name: 'Warning (50-69)', value: distribution.warning, color: '#F59E0B' },
      { name: 'Poor (<50)', value: distribution.poor, color: '#EF4444' }
    ]
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/3 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-300 h-24 rounded-lg"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-300 h-64 rounded-lg"></div>
              <div className="bg-gray-300 h-64 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <Link
                to="/admin"
                className="text-blue-600 hover:text-blue-900 text-sm font-medium"
              >
                ← Back to Dashboard
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">
                Portfolio Analytics
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex space-x-4">
                <select
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="current">Current Month</option>
                  <option value="last3months">Last 3 Months</option>
                  <option value="last12months">Last 12 Months</option>
                </select>

                <select
                  value={selectedView}
                  onChange={(e) => setSelectedView(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="overview">Overview</option>
                  <option value="performance">Performance Analysis</option>
                  <option value="industry">Industry Comparison</option>
                </select>
              </div>
              <UserEmailBadge email={user?.email} />
              <button
                onClick={handleLogout}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold" aria-hidden="true">
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <rect x="4" y="5" width="7" height="13" rx="1" fill="currentColor" opacity="0.85" />
                      <rect x="13" y="3" width="7" height="15" rx="1" fill="currentColor" opacity="0.65" />
                      <path d="M6.5 8.5h2M6.5 11.5h2M15.5 6.5h2M15.5 9.5h2M15.5 12.5h2" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Businesses</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {aggregateData?.activeBusinesses || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold" aria-hidden="true">
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.85" />
                      <path
                        d="M13.5 8.5h-3a1.8 1.8 0 000 3.6h1a1.9 1.9 0 010 3.8h-3.2"
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                      <path d="M12 6.5v11" stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(aggregateData?.totalRevenue || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold" aria-hidden="true">
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <path
                        d="M13.5 3.5L7 13.5h4l-1.5 7 6.5-10h-4l1.5-7z"
                        fill="#ffffff"
                      />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Avg Health Score</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {aggregateData?.averageHealthScore || 0}/100
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                  <span className="text-white text-sm font-bold" aria-hidden="true">
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <rect x="5" y="11" width="3" height="7" rx="0.8" fill="#ffffff" opacity="0.8" />
                      <rect x="10.5" y="8" width="3" height="10" rx="0.8" fill="#ffffff" opacity="0.9" />
                      <rect x="16" y="6" width="3" height="12" rx="0.8" fill="#ffffff" opacity="0.7" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Net Profit</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatCurrency((aggregateData?.totalRevenue || 0) - (aggregateData?.totalExpenses || 0))}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Performance Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={aggregateData?.performanceDistribution || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(aggregateData?.performanceDistribution || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [value, 'Businesses']} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Industry Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Industry Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={aggregateData?.industryBreakdown || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'avgHealthScore' ? `${value}/100` : 
                    name === 'count' ? value :
                    formatCurrency(value),
                    name === 'avgHealthScore' ? 'Avg Health Score' :
                    name === 'count' ? 'Count' : 'Avg Revenue'
                  ]}
                />
                <Legend />
                <Bar dataKey="count" fill="#3B82F6" name="Businesses" />
                <Bar dataKey="avgHealthScore" fill="#10B981" name="Health Score" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top and Bottom Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performers */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Top Performers</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {(aggregateData?.topPerformers || []).map((business, index) => (
                  <div key={business.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">#{index + 1} {business.business_name}</p>
                      <p className="text-sm text-gray-600">{business.industry}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">{business.healthScore}/100</p>
                      <p className="text-sm text-gray-500">{formatCurrency(business.totalRevenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Performers */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Needs Attention</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {(aggregateData?.bottomPerformers || []).map((business, index) => (
                  <div key={business.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{business.business_name}</p>
                      <p className="text-sm text-gray-600">{business.industry}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">{business.healthScore}/100</p>
                      <p className="text-sm text-gray-500">{formatCurrency(business.totalRevenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminAnalyticsDashboard
